<template>
  <div>
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <nuxt-link :to="localePath('office-orders')">
            {{
            $t("page.orders")
            }}
          </nuxt-link>
        </li>

        <li class="is-active">
          <a aria-current="page">{{ active.order_number }}</a>
        </li>
      </ul>
    </nav>

    <nav class="panel">
      <p class="panel-heading">{{ $t('form.order_number') + ': '+ active.order_number }}</p>
      <div class="panel-block">
        <table class="table is-striped is-fullwidth">
          <tbody>
            <tr>
              <td>{{ $t("form.status") }}:</td>
              <td>{{ active.latest }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.company") }}:</td>
              <td>{{ active.company }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.user") }}:</td>
              <td>
                <nuxt-link
                  :to="localePath({ name: 'office-users-id', params: { id: active.user_id }})"
                >{{ active.user_id }}</nuxt-link>
              </td>
            </tr>
            <tr>
              <td>{{ $t("form.date") }}:</td>
              <td>{{ active.created_at }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.paid_at") }}:</td>
              <td>{{ active.paid_at }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.delivery_time") }}:</td>
              <td>{{ active.delivery_time }} {{$t('form.days')}}</td>
            </tr>
            <tr>
              <td>{{ $t("form.price") }}:</td>
              <td>{{ active.price_without_comission | currency('eur') }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.price_with_commission") }}:</td>
              <td>{{ active.price_with_comission | currency('eur') }}</td>
            </tr>
            <tr>
              <td>{{ $t("form.price_with_tax") }}:</td>
              <td>{{ active.price_with_tax | currency('eur') }}</td>
            </tr>
            <tr v-if="active.xml">
              <td>{{ $t("form.xml") }}:</td>
              <td>
                <a
                  class="link"
                  :href="`${active.xml}`"
                  download
                  target="_blank"
                >{{ $t("form.download_xml") }}</a>
              </td>
            </tr>
            <tr v-if="active.dxf">
              <td>{{ $t("form.dxf") }}:</td>
              <td>
                <a
                  class="link"
                  :href="`${active.dxf}`"
                  download
                  target="_blank"
                >{{ $t("form.download_dxf") }}</a>
              </td>
            </tr>
            <tr v-if="active.order">
              <td>{{ $t("form.order") }}:</td>
              <td>
                <a class="link" :style="{ color: 'orange' }" :href="`${active.order}`" target="_blank">{{ $t("form.order_pdf") }}</a>
              </td>
            </tr>
            <tr v-if="active.order_for_fittings">
              <td>{{ $t("form.order_for_fittings") }}:</td>
              <td>
                <a
                  class="link"
                  :href="`${active.order_for_fittings}`"
                  target="_blank"
                >{{ $t("form.order_for_fittings_pdf") }}</a>
              </td>
            </tr>
            <tr v-if="active.invoice">
              <td>{{ $t("form.invoice") }}:</td>
              <td>
                <a
                  class="link"
                  :style="{ color: '#48d848' }" 
                  :href="`${active.invoice}`"
                  target="_blank"
                >{{ $t("form.invoice_pdf") }}</a>
              </td>
            </tr>
            <tr v-if="active.shapes">
              <td>{{ $t("form.shapes") }}:</td>
              <td>
                <a
                  class="link"
                  :href="`${active.shapes}`"
                  download
                  target="_blank"
                >{{ $t("form.download_shapes") }}</a>
              </td>
            </tr>
            <tr v-if="active.preview">
              <td>{{ $t("form.preview") }}:</td>
              <td>
                <a
                  class="link"
                  :href="`${active.preview}`"
                  target="_blank"
                >{{ $t("form.preview_pdf") }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </nav>
    <div class="is-clearfix">
      <button
        v-if="active.latest !== 'paid'"
        class="button is-primary"
        @click="markAsPaid"
        :class="{'is-loading': updateLoading}"
        :disabled="updateLoading"
        style="margin-right: 15px;"
      >{{$t('form.mark_as_paid')}}</button>

      <button
        class="button is-primary"
        @click="resend"
        :class="{'is-loading': resendLoading}"
        :disabled="resendLoading"
      >{{$t('form.resend')}}</button>
      
      <button
        class="button is-danger is-pulled-right m-l-3"
        @click="removeOrder"
        :class="{'is-loading': removeLoading}"
        :disabled="removeLoading"
      >{{$t('form.remove')}}</button>
    </div>

    <div class="m-t-5">
      <h3 class="title is-3">{{ $t('form.files') }}</h3>
      <div class="m-b-5">
        <form @submit.prevent="onFilesSubmit">
          <div class="is-clearfix">
            <field-file-multiple
              class="is-pulled-left m-t-2 m-r-2"
              required
              name="files"
              :value.sync="files"
              :disabled="uploadFilesLoading !== false"
              :label="$t('form.choose_files')"
            />
            <button
              v-if="files.length"
              class="button is-pulled-left is-primary m-t-2 m-r-2"
              :class="{ 'is-loading': uploadFilesLoading }"
              :disabled="uploadFilesLoading !== false"
            >{{ $t("form.upload") }}</button>
            <button
              v-if="files.length"
              @click="clearFiles"
              :disabled="uploadFilesLoading !== false"
              class="button is-primary m-t-2"
            >{{ $t("form.clear") }}</button>
          </div>
        </form>
      </div>
      <div class="m-b-3" v-for="file in active.files" :key="file.id">
        <div class="file">
          <a :href="file.uri" download>{{file.filename}}</a>
          <button
            class="button is-danger is-small m-l-3"
            :class="{'is-loading': removeFileLoading == file.id }"
            @click="removeFile(file.id)"
          >
            <i class="mdi mdi-trash-can-outline"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  async asyncData({ store, params, error }) {
    try {
      await store.dispatch("orders/setActiveOrder", params.id);
    } catch (e) {
      error(e);
    }
  },

  data() {
    return {
      updateLoading: false,
      removeLoading: false,
      removeFileLoading: false,
      resendLoading: false,
      uploadFilesLoading: false,
      index: null,
      files: []
    };
  },

  computed: {
    active() {
      return this.$store.state.orders.active;
    }
  },

  methods: {
    async removeOrder() {
      if (confirm(this.$t("message.are_you_sure"))) {
        this.removeLoading = true;
        try {
          await this.$api.deleteOrder(this.active.id);
          this.$router.replace(this.localePath("office-orders"));
        } catch (e) {
          console.log(e);
          this.handleFormError(e.response.data);
        } finally {
          this.removeLoading = false;
        }

      }
    },

    async removeFile(file) {
      if (confirm(this.$t("message.are_you_sure"))) {
        this.removeFileLoading = file;
        try {
          await this.$api.deleteOrderFile(file);
          this.$store.commit('orders/REMOVE_ORDER_FILE', { file, order: this.active.id })
        } catch (e) {
          console.log(e);
          this.handleFormError(e.response.data);
        } finally {
          this.removeFileLoading = false;
        }

      }
    },

    async markAsPaid() {
      if (confirm(this.$t("message.are_you_sure"))) {
        this.updateLoading = true;
        try {
          await this.$api.payOrder(this.active.id);

          this.$store.commit("orders/SET_ACTIVE", {
            ...this.active,
            latest: "paid"
          });
        } catch (e) {
          console.log(e);
          this.handleFormError(e.response.data);
        } finally {
          this.updateLoading = false;
        }
      }
    },

    async resend() {
      if (confirm(this.$t("message.are_you_sure"))) {
        this.resendLoading = true;
        try {
          const { data } = await this.$api.resendOrder(this.active.id);
          this.$toast.success(data.message);
        } catch (e) {
          console.log(e);
          this.handleFormError(e.response.data);
        } finally {
          this.resendLoading = false;
        }
      }
    },

    clearFiles() {
      this.files = [];
    },

    async onFilesSubmit() {
      const validation = await this.$validator.validateAll();
      if (!validation) return;

      this.uploadFilesLoading = true;

      const formData = new FormData();

      Array.from(this.files).forEach(e => {
        formData.append("files[]", e, e.name);
      });
      

      try {
        const { data } = await this.$api.uploadOrderFiles(this.active.id, formData);
        await this.$store.dispatch('orders/setActiveOrder', this.active.id);
        this.clearFiles();
        this.$toast.success(data.message);
      } catch (e) {
        console.log(e);
        this.handleFormError(e.response.data);
      } finally {
        this.uploadFilesLoading = false;
      }

    }
  }
};
</script>

<style lang="scss" scoped>
.file {
  display: inline-block;

  .button {
    opacity: 0;
    pointer-events: none;
  }

  &:hover {
    button {
      opacity: 1;
      pointer-events: all;
    }
  }
}
</style>


// WEBPACK FOOTER //
// resources/nuxt/pages/office/orders/_id.vue